import * as React from 'react';
import { Box } from '@mui/material';

type Variant = { label: string; color: string };

/**
 * The six variants of the `file_type` component set, keyed by extension.
 *
 * Colours are literals rather than palette variables on purpose: they identify
 * a file format, so they must not follow the company space's primary colour
 * the way the rest of Klara does. DOC covers doc and docx, PPT ppt and pptx,
 * JPG jpg and jpeg — the design draws one badge per format, not per extension.
 */
const VARIANTS: Record<string, Variant> = {
  pdf: { label: 'PDF', color: '#ef4444' },
  doc: { label: 'DOC', color: '#3b82f6' },
  docx: { label: 'DOC', color: '#3b82f6' },
  ppt: { label: 'PPT', color: '#f59e0b' },
  pptx: { label: 'PPT', color: '#f59e0b' },
  png: { label: 'PNG', color: '#67c07f' },
  jpg: { label: 'JPG', color: '#67c07f' },
  jpeg: { label: 'JPG', color: '#67c07f' },
};

/** Anything the design has no badge for — an xlsx, or a name with no suffix. */
const UNKNOWN: Variant = { label: '?', color: '#b8b8b8' };

function variantOf(fileName: string): Variant {
  const parts = fileName.toLowerCase().split('.');
  if (parts.length < 2) return UNKNOWN;

  return VARIANTS[parts[parts.length - 1]] ?? UNKNOWN;
}

/**
 * The format badge on a source file row.
 *
 * Decorative: the extension it names is already in the filename beside it, so
 * a screen reader gains nothing from reading it twice.
 */
export function FileTypeBadge({ fileName }: { fileName: string }) {
  const variant = variantOf(fileName);
  const unknown = variant === UNKNOWN;

  return (
    <Box
      aria-hidden
      sx={{
        width: '28px',
        height: '28px',
        borderRadius: '4px',
        flexShrink: 0,
        backgroundColor: variant.color,
        color: 'var(--color-white)',
        // Three bold uppercase glyphs at 10px sit inside 28px with room to
        // spare; the 8px the mock measures at is below anything else in the
        // app and turns the badge into a coloured smudge at 1x.
        fontSize: unknown ? '20px' : '10px',
        fontWeight: unknown ? 600 : 700,
        lineHeight: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {variant.label}
    </Box>
  );
}
